import { useEffect, useId, useRef, useState } from 'react';
import { readStreak, currentStreak, clearsToday } from './streaks.js';
import GameStats, { STATS_EVENT } from './GameStats.js';

/**
 * The outer silhouette: round belly, curled tip, and a deep notch on the left
 * where the lick folds back over itself. No spikes anywhere on it - a rounder
 * flame than the emoji, and one that looks the same on every platform because
 * it is drawn rather than borrowed from the system font.
 */
const FLAME_PATH = 'M13.3 1.4c1.4 3.5.6 6-1.2 8.2-1.9 2.3-4.1 3.8-4.1 6.3a6 6 0 0 0 12 .1c0-2.5-1.4-4.5-3-6.1-.2 1.5-.9 2.4-2 2.9 1.6-3.7.8-7.8-1.7-11.4Z';

/** The hot core, cut out of the belly. Wavy top, echoing the outer curl. */
const CORE_PATH = 'M14 13.3c1.1 1.4 1.4 2.3 2.3 3 .9.8 1.5 1.7 1.5 2.8a3.8 3.8 0 0 1-7.6 0c0-1.1.6-2 1.5-2.8.9-.7 1.2-1.6 2.3-3Z';

/**
 * A flame in the tiles' own gold, falling through amber to a burnt edge at the
 * base, so the emblem reads as part of the board.
 *
 * The core is punched out with a mask rather than painted a second colour: what
 * the emblem is sitting on shows through it, which is what makes one shape read
 * as two layers. A mask rather than an even-odd subpath because the core hangs
 * slightly past the belly, and the overhang has to be ignored rather than
 * carving a notch out of the silhouette.
 */
const FlameEmblem = ({ size = 24 }) => {
  // Several of these are on screen at once, and two SVGs sharing an id means the
  // second one quietly paints itself with the first one's gradient
  const uid = useId().replace(/:/g, '');
  const gradient = `streak-flame-${uid}`;
  const core = `streak-core-${uid}`;

  return (
    <svg className="streak-flame" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradient} x1="38%" y1="0%" x2="62%" y2="100%">
          <stop offset="0%" stopColor="#FFE7A8" />
          <stop offset="26%" stopColor="#FFC55E" />
          <stop offset="58%" stopColor="#F0A63F" />
          <stop offset="82%" stopColor="#E07C22" />
          <stop offset="100%" stopColor="#C25E14" />
        </linearGradient>
        <mask id={core}>
          <path d={FLAME_PATH} fill="#fff" />
          <path d={CORE_PATH} fill="#000" />
        </mask>
      </defs>
      <path d={FLAME_PATH} fill={`url(#${gradient})`} mask={`url(#${core})`} />
    </svg>
  );
};

/** Kept in step with the keyframes in index.css */
const FLARE_MS = 900;

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const TrophyOutline = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
    <path d="M8 5H5v1a3 3 0 0 0 3 3M16 5h3v1a3 3 0 0 1-3 3" />
    <path d="M12 13v4M9 20h6" />
  </svg>
);

/**
 * What a streak is, and where this one stands. Opened from the card, because a
 * bare number in the corner raises the question and nothing else answers it.
 *
 * Deliberately built from the purchase sheet's own classes rather than its own
 * copies of them - see Unlimited.css, where each rule now carries both names.
 * These are the app's two panels and they should not be able to drift apart.
 */
function StreakInfo({ streak, onClose }) {
  const { current, longest, today } = streak;

  return (
    <div className="hud-overlay" onClick={onClose}>
      <div className="streak-card-hud hud" onClick={(e) => e.stopPropagation()}>
        <button className="hud-close" onClick={onClose} aria-label="Close">&#x2715;</button>

        <div className="streak-head">
          <span className="streak-badge">Daily Streak</span>
          {/* The figure and the mark, nothing else. The label carries the words
              a screen reader needs, since a bare "4" says nothing on its own. */}
          <h2 className="streak-figure" aria-label={`${plural(current, 'day')} streak`}>
            <span className="streak-figure-count">{current}</span>
            <FlameEmblem size={30} />
          </h2>
          {/* The one line here they can act on today */}
          <p className="streak-sub">
            {today ? 'Today counts. Come back tomorrow.' : 'Clear a board today to keep it going.'}
          </p>
        </div>

        <ul className="streak-perks">
          <li>
            <span className="streak-perk-icon"><FlameEmblem size={22} /></span>
            <span className="streak-perk-body">
              <span className="streak-perk-title">How it works</span>
              <span className="streak-perk-detail">
                Clear one board a day. Running out of moves does not count.
                Miss a day and it resets.
              </span>
            </span>
          </li>
          <li>
            <span className="streak-perk-icon"><TrophyOutline /></span>
            <span className="streak-perk-body">
              <span className="streak-perk-title">Best streak</span>
              <span className="streak-perk-detail">
                {longest === 0
                  ? 'None yet. Today can be the first.'
                  : longest === current
                    ? 'This one. Your longest yet.'
                    : `${plural(longest, 'day')} in a row.`}
              </span>
            </span>
          </li>
        </ul>

        {/* The panel is a read, not a decision - so the way out is the one
            button on it, in the shape the buy button already established */}
        <button className="streak-continue" onClick={onClose}>Continue</button>
      </div>
    </div>
  );
}

const readEarned = (now = Date.now()) => {
  const entries = GameStats.getHistoryEntries();
  return {
    current: currentStreak(entries, now),
    // The clear that just landed is today's first, so it is the one that moved
    // the count. A second clear on the same day changes nothing.
    counted: clearsToday(entries, now) === 1,
  };
};

/**
 * The streak, said on the clear screen itself.
 *
 * The card in the corner already flares when the count goes up, but nobody is
 * looking at the corner in the second a board empties - they are looking at the
 * trophy in the middle. Saying it here is what makes the connection between
 * "I cleared a board" and "my streak went up" land at all.
 */
export function StreakEarned() {
  const [earned, setEarned] = useState(readEarned);

  useEffect(() => {
    const refresh = () => setEarned(readEarned());
    // The clear is recorded in Board's effect, which runs after this one - so
    // the first read here is a board behind, and the event is what corrects it
    refresh();
    window.addEventListener(STATS_EVENT, refresh);
    return () => window.removeEventListener(STATS_EVENT, refresh);
  }, []);

  // Nothing true to say - a tutorial board is kept out of history, so a lesson
  // cleared on a fresh install would otherwise announce a streak of zero
  if (earned.current === 0) return null;

  // Only the clear that moved the count says anything. Telling the second and
  // third board of the day that they changed nothing is a flat note on a win
  // screen, and it is not information they can act on - the corner card is
  // still there for anyone who wants to look.
  if (!earned.counted) return null;

  return (
    <div className="streak-earned">
      <span className="streak-earned-figure">
        <span className="streak-earned-count">{earned.current}</span>
        <FlameEmblem size={26} />
      </span>
      {/* The figure above is the number, so this only has to say what it is
          counting. Day one gets words instead, where "days in a row" would be
          a unit with nothing to compare against yet. */}
      <span className="streak-earned-label">
        {earned.current === 1 ? 'Streak started' : 'Days in a row'}
      </span>
    </div>
  );
}

/**
 * Days in a row with at least one board cleared, in the corner the Help button
 * used to hold. Derived on every read rather than kept in state anywhere else -
 * see streaks.js - so it cannot drift from the history it describes.
 */
function StreakCard() {
  const [streak, setStreak] = useState(() => readStreak());
  const [showInfo, setShowInfo] = useState(false);
  // Bumped when the count goes up, purely to restart the flare
  const [flare, setFlare] = useState(0);
  const cardRef = useRef(null);
  const lastCurrent = useRef(streak.current);

  useEffect(() => {
    const refresh = () => {
      const next = readStreak();
      // Only when it actually rises. Clearing a second board on a day already
      // counted changes nothing, and celebrating it would be a lie.
      if (next.current > lastCurrent.current) setFlare((count) => count + 1);
      lastCurrent.current = next.current;
      setStreak(next);
    };
    // A clear lands here, and so does an undo rewinding one back out
    window.addEventListener(STATS_EVENT, refresh);
    return () => window.removeEventListener(STATS_EVENT, refresh);
  }, []);

  // Driven by hand rather than by a React class, because the same animation has
  // to be able to play twice in a row - see waveCells in Board for the same
  // remove/reflow/add dance. Without the reflow the browser coalesces the two
  // class changes and nothing restarts.
  useEffect(() => {
    const node = cardRef.current;
    if (!flare || !node) return undefined;
    node.classList.remove('streak-card-flare');
    void node.offsetWidth;
    node.classList.add('streak-card-flare');
    const timer = setTimeout(() => node.classList.remove('streak-card-flare'), FLARE_MS);
    return () => clearTimeout(timer);
  }, [flare]);

  const running = streak.current > 0;
  const label = !running
    ? 'No streak yet. What is this?'
    : streak.longest > streak.current
      ? `${plural(streak.current, 'day')} streak. Best: ${plural(streak.longest, 'day')}.`
      : `${plural(streak.current, 'day')} streak, your best yet.`;

  return (
    <>
      {/* The button is the card, positioned directly rather than sitting inside
          a wrapper - the mode selector opposite is built the same way, and a
          wrapper's padding here would push this one out of line with it */}
      <button
        ref={cardRef}
        className={'invisible-hud streak-card' + (running ? '' : ' streak-card-unlit')}
        onClick={() => setShowInfo(true)}
        aria-label={running ? `${label} What is this?` : label}
      >
        {/* Number first: it is the thing being read, and the flame is the
            mark that says what the number means */}
        <span className="streak-count">{streak.current}</span>
        <FlameEmblem />
      </button>
      {showInfo && <StreakInfo streak={streak} onClose={() => setShowInfo(false)} />}
    </>
  );
}

export default StreakCard;
